import * as React from "react";
import { debounce } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal, Button, Input, FormGroup, Label } from "../components/Modal";
import * as UserStore from "../users/UserStore";
import PermissionBrowser from "../identityPermissions/PermissionBrowser";
import { GetContext, UnsubscribeContext } from "../general/ApplicationContext";
import * as PermissionStore from "../identityPermissions/IdentityPermissionStore";
import * as AccountStore from "../account/AccountStore";
import * as OrganizationStore from "./OrganizationStore";

const { Component } = React;

interface Role {
    id: string;
    name: string;
    organizationId: string;
    rules?: Array<{ id: string; rule: string }>;
    permissions?: Array<{ id: string; permission: string; effect: number | string }>;
}

interface RoleGroupOption {
    id: string;
    name: string;
}

interface OrganizationRolesState {
    roles: Array<Role>;
    selectedRole?: Role;
    showCreateRoleModal: boolean;
    showEditRoleModal: boolean;
    showAssignRoleModal: boolean;
    showRolePermissionsModal: boolean;
    showViewUsersModal: boolean;
    showManageRulesModal: boolean;
    roleUsers: Array<any>;
    ruleType: string;
    newRoleName: string;
    editRoleName: string;
    searchQuery: string;
    users: Array<any>;
    groups: RoleGroupOption[];
    groupSearchQuery: string;
    groupsLoading: boolean;
    groupsError: string;
    currentUser: any;
    userPolicies: any;
    loading: boolean;
}

interface OrganizationRolesProps {
    organization: string;
}

let contextSubscriptionId = "";

export default class OrganizationRoles extends Component<OrganizationRolesProps, OrganizationRolesState> {
    debouncedSearchUsers: any;

    constructor(props: OrganizationRolesProps) {
        super(props);
        this.state = {
            roles: [],
            selectedRole: undefined,
            showCreateRoleModal: false,
            showEditRoleModal: false,
            showAssignRoleModal: false,
            showRolePermissionsModal: false,
            showViewUsersModal: false,
            showManageRulesModal: false,
            roleUsers: [],
            ruleType: "user",
            newRoleName: "",
            editRoleName: "",
            searchQuery: "",
            users: [],
            groups: [],
            groupSearchQuery: "",
            groupsLoading: false,
            groupsError: "",
            currentUser: undefined,
            userPolicies: undefined,
            loading: true,
        };
    }

    isAdmin() {
        if (this.state.currentUser) {
            return this.state.currentUser.claims.filter((c: any) => c.type == "http://schemas.microsoft.com/ws/2008/06/identity/claims/role" && c.value == "Admin").length > 0;
        }
        return false;
    }

    hasPermission(permission: string) {
        if (this.state.currentUser && this.state.userPolicies) {
            if (this.isAdmin()) {
                return true;
            } else {
                return this.state.userPolicies.filter((policy: any) => policy.permission == permission).length > 0;
            }
        }
        return false;
    }

    canGrantPermission(permission: string) {
        // Admins can grant any permission
        if (this.isAdmin()) {
            return true;
        }
        // Users with manageUserPermissions can only grant permissions they have
        if (this.hasPermission("manageUserPermissions")) {
            return this.hasPermission(permission);
        }
        return false;
    }

    componentDidMount() {
        AccountStore.GetCurrentLoggedInUser().then((user) => {
            this.setState({ currentUser: user });
        });
        PermissionStore.GetMyPermissions().then((response) => {
            this.setState({ userPolicies: response.data });
        });

        this.debouncedSearchUsers = debounce((query: string, skip: number, limit: number) => {
            return UserStore.SearchUsers(query, skip, limit, true, true, true, true).then((response) => {
                this.setState({ users: response.data });
                return response;
            });
        }, 300);

        contextSubscriptionId = GetContext((context: any) => {
            this.loadRoles();
        });
    }

    componentWillUnmount() {
        UnsubscribeContext(contextSubscriptionId);
    }

    loadRoles = async () => {
        this.setState({ loading: true });
        try {
            const response = await OrganizationStore.getRoles(this.props.organization);
            this.setState({ roles: response.data, loading: false });
        } catch (error) {
            console.error("Failed to load roles:", error);
            this.setState({ loading: false });
        }
    };

    createRole = async () => {
        if (!this.state.newRoleName.trim()) return;

        try {
            await OrganizationStore.createRole(this.props.organization, this.state.newRoleName);
            await this.loadRoles();
            this.setState({
                showCreateRoleModal: false,
                newRoleName: "",
            });
        } catch (error) {
            console.error("Failed to create role:", error);
        }
    };

    updateRole = async () => {
        if (!this.state.selectedRole || !this.state.editRoleName.trim()) return;

        try {
            await OrganizationStore.updateRole(this.state.selectedRole.id, this.props.organization, this.state.editRoleName);
            await this.loadRoles();
            this.setState({
                showEditRoleModal: false,
                editRoleName: "",
                selectedRole: undefined,
            });
        } catch (error) {
            console.error("Failed to update role:", error);
        }
    };

    deleteRole = async (roleId: string) => {
        if (!confirm("Are you sure you want to delete this role?")) return;

        try {
            await OrganizationStore.deleteRole(roleId, this.props.organization);
            await this.loadRoles();
        } catch (error) {
            console.error("Failed to delete role:", error);
        }
    };

    loadRoleDetails = async (roleId: string) => {
        try {
            const response = await OrganizationStore.getRole(roleId, this.props.organization);
            this.setState({ selectedRole: response.data });
        } catch (error) {
            console.error("Failed to load role details:", error);
        }
    };

    handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        var query = e.target.value;
        this.setState({ searchQuery: query }, () => {
            if (this.state.searchQuery.length > 0) {
                this.debouncedSearchUsers(this.state.searchQuery, 0, 25);
            }
        });
    };

    startEditRole = (role: Role) => {
        this.setState({
            selectedRole: role,
            editRoleName: role.name,
            showEditRoleModal: true,
        });
    };

    startManageRolePermissions = async (role: Role) => {
        // Load full role details first
        const response = await OrganizationStore.getRole(role.id, this.props.organization);
        const loadedRole = response.data;

        this.setState({
            selectedRole: loadedRole,
            showRolePermissionsModal: true,
        });
    };

    startAssignRole = (role: Role) => {
        this.setState({
            selectedRole: role,
            showAssignRoleModal: true,
            searchQuery: "",
            users: [],
            groups: [],
            groupSearchQuery: "",
            groupsError: "",
            ruleType: "user",
        });
    };

    loadGroups = async () => {
        this.setState({ groupsLoading: true, groupsError: "" });
        try {
            const response = await OrganizationStore.getRoleGroupOptions(this.props.organization);
            this.setState({ groups: response.data, groupsLoading: false });
        } catch (error) {
            console.error("Failed to load groups:", error);
            this.setState({ groups: [], groupsLoading: false, groupsError: "Could not load groups." });
        }
    };

    changeRuleType = (ruleType: string) => {
        this.setState({ ruleType, searchQuery: "", users: [], groupSearchQuery: "" });
        if (ruleType === "group") this.loadGroups();
    };

    addUserRuleToRole = async (userId: string, userName: string) => {
        if (!this.state.selectedRole) return;

        try {
            const rule = `${userId}:User`;
            await OrganizationStore.addRuleToRole(this.state.selectedRole.id, this.props.organization, rule);
            // Remove user from search list after adding rule
            this.setState((prevState) => ({
                users: prevState.users.filter((u) => u.id !== userId),
            }));
            // Reload role to show updated rules
            await this.loadRoleDetails(this.state.selectedRole.id);
        } catch (error) {
            console.error("Failed to add user rule to role:", error);
        }
    };

    addGroupRuleToRole = async (groupId: string) => {
        if (!this.state.selectedRole) return;

        try {
            const rule = `${groupId}:Group`;
            await OrganizationStore.addRuleToRole(this.state.selectedRole.id, this.props.organization, rule);
            await this.loadRoleDetails(this.state.selectedRole.id);
            this.setState((prevState) => ({ groups: prevState.groups.filter((group) => group.id !== groupId) }));
        } catch (error) {
            console.error("Failed to add group rule to role:", error);
        }
    };

    addMembershipRuleToRole = async (membershipType: string) => {
        if (!this.state.selectedRole) return;

        try {
            await OrganizationStore.addRuleToRole(this.state.selectedRole.id, this.props.organization, `${this.props.organization}:${membershipType}`);
            await this.loadRoleDetails(this.state.selectedRole.id);
        } catch (error) {
            console.error("Failed to add membership rule to role:", error);
        }
    };

    addUniversalRuleToRole = async () => {
        if (!this.state.selectedRole) return;

        try {
            await OrganizationStore.addRuleToRole(this.state.selectedRole.id, this.props.organization, "*");
            await this.loadRoleDetails(this.state.selectedRole.id);
        } catch (error) {
            console.error("Failed to add universal rule to role:", error);
        }
    };

    getAvailableGroups = () => this.state.groups.filter((group) =>
        group.name.toLowerCase().includes(this.state.groupSearchQuery.toLowerCase()) &&
        !this.state.selectedRole?.rules?.some((rule) => rule.rule === `${group.id}:Group`));

    startManageRules = async (role: Role) => {
        await this.loadRoleDetails(role.id);
        this.setState({
            showManageRulesModal: true,
        });
    };

    removeRuleFromRole = async (ruleId: string) => {
        if (!this.state.selectedRole) return;

        if (!confirm("Are you sure you want to remove this rule?")) return;

        try {
            await OrganizationStore.removeRuleFromRole(this.state.selectedRole.id, this.props.organization, ruleId);
            await this.loadRoleDetails(this.state.selectedRole.id);
        } catch (error) {
            console.error("Failed to remove rule from role:", error);
        }
    };

    startViewUsers = async (role: Role) => {
        try {
            const response = await OrganizationStore.evaluateRole(role.id, this.props.organization);
            this.setState({
                selectedRole: role,
                roleUsers: response.data.users || [],
                showViewUsersModal: true,
            });
        } catch (error) {
            console.error("Failed to evaluate role:", error);
        }
    };

    handlePermissionChange = async (permission: string, assigned: boolean) => {
        const role = this.state.selectedRole;
        if (!role || !this.canGrantPermission(permission)) return;
        if (assigned) {
            await OrganizationStore.addPermissionToRole(role.id, this.props.organization, permission, "Allow");
        } else {
            const rolePermission = role.permissions?.find(p => p.permission === permission);
            if (rolePermission) {
                await OrganizationStore.removePermissionFromRole(role.id, this.props.organization, rolePermission.id);
            }
        }
        const response = await OrganizationStore.getRole(role.id, this.props.organization);
        if (this.state.selectedRole?.id === role.id) this.setState({ selectedRole: response.data });
        await this.loadRoles();
    };

    toggleCreateRoleModal = () => {
        this.setState((prevState) => ({
            showCreateRoleModal: !prevState.showCreateRoleModal,
            newRoleName: "",
        }));
    };

    toggleEditRoleModal = () => {
        this.setState((prevState) => ({
            showEditRoleModal: !prevState.showEditRoleModal,
            editRoleName: "",
            selectedRole: undefined,
        }));
    };

    toggleAssignRoleModal = () => {
        this.setState((prevState) => ({
            showAssignRoleModal: !prevState.showAssignRoleModal,
            selectedRole: undefined,
            searchQuery: "",
            users: [],
            groups: [],
            groupSearchQuery: "",
            groupsError: "",
        }));
    };

    toggleRolePermissionsModal = () => {
        this.setState((prevState) => ({
            showRolePermissionsModal: !prevState.showRolePermissionsModal,
            selectedRole: undefined,
        }));
    };

    toggleViewUsersModal = () => {
        this.setState((prevState) => ({
            showViewUsersModal: !prevState.showViewUsersModal,
            selectedRole: undefined,
            roleUsers: [],
        }));
    };

    toggleManageRulesModal = () => {
        this.setState((prevState) => ({
            showManageRulesModal: !prevState.showManageRulesModal,
            selectedRole: undefined,
        }));
    };

    render() {
        if (this.state.loading) {
            return (
                <div className="flex items-center justify-center py-12">
                    <FontAwesomeIcon icon={["fad", "spinner"]} className="text-4xl text-blue-500 animate-spin" />
                    <span className="ml-3 text-gray-600">Loading roles...</span>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                {/* Header Section */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={["fad", "user-shield"]} className="text-3xl text-purple-500" />
                        <div>
                            <h2 className="text-2xl font-semibold text-gray-800">Role Management</h2>
                            <p className="text-sm text-gray-600">Create and manage roles with permissions for your organization</p>
                        </div>
                    </div>
                    {this.hasPermission("manageUserPermissions") && (
                        <Button variant="primary" onClick={this.toggleCreateRoleModal}>
                            <FontAwesomeIcon icon={["fad", "plus"]} className="mr-2" />
                            Create Role
                        </Button>
                    )}
                </div>

                {/* Roles Table */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permissions</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {this.state.roles.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="px-6 py-12 text-center">
                                        <FontAwesomeIcon icon={["fad", "user-shield"]} className="text-4xl text-gray-300 mb-3" />
                                        <p className="text-gray-500">No roles found</p>
                                        <p className="text-sm text-gray-400 mt-1">Create your first role to get started</p>
                                    </td>
                                </tr>
                            ) : (
                                this.state.roles.map((role) => (
                                    <tr key={role.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-semibold">
                                                    {role.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="font-medium text-gray-900">{role.name}</div>
                                                    <div className="text-sm text-gray-500">Role ID: {role.id}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900">{role.permissions?.length || 0} permissions assigned</div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button variant="secondary" size="sm" onClick={() => this.startManageRolePermissions(role)}>
                                                    <FontAwesomeIcon icon={["fad", "shield-check"]} className="mr-1" />
                                                    Permissions
                                                </Button>
                                                <Button variant="outline-secondary" size="sm" onClick={() => this.startManageRules(role)}>
                                                    <FontAwesomeIcon icon={["fad", "list-check"]} className="mr-1" />
                                                    Rules
                                                </Button>
                                                <Button variant="outline-secondary" size="sm" onClick={() => this.startViewUsers(role)}>
                                                    <FontAwesomeIcon icon={["fad", "users"]} className="mr-1" />
                                                    View Users
                                                </Button>
                                                {this.hasPermission("manageUserPermissions") && (
                                                    <>
                                                        <Button variant="primary" size="sm" onClick={() => this.startAssignRole(role)}>
                                                            <FontAwesomeIcon icon={["fad", "user-plus"]} className="mr-1" />
                                                            Assign
                                                        </Button>
                                                        <Button variant="outline-secondary" size="sm" onClick={() => this.startEditRole(role)}>
                                                            <FontAwesomeIcon icon={["fad", "edit"]} className="mr-1" />
                                                            Edit
                                                        </Button>
                                                        <button
                                                            type="button"
                                                            className="inline-flex items-center px-2.5 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                                            onClick={() => this.deleteRole(role.id)}
                                                        >
                                                            <FontAwesomeIcon icon={["fad", "trash"]} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Create Role Modal */}
                <Modal isOpen={this.state.showCreateRoleModal} onClose={this.toggleCreateRoleModal} title="Create New Role" icon={["fad", "user-shield"]}>
                    <div className="p-6">
                        <FormGroup>
                            <Label>Role Name</Label>
                            <Input type="text" value={this.state.newRoleName} onChange={(e) => this.setState({ newRoleName: e.target.value })} placeholder="Enter role name" />
                        </FormGroup>

                        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                            <Button variant="secondary" onClick={this.toggleCreateRoleModal}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={this.createRole}>
                                <FontAwesomeIcon icon={["fad", "plus"]} className="mr-2" />
                                Create Role
                            </Button>
                        </div>
                    </div>
                </Modal>

                {/* Edit Role Modal */}
                <Modal isOpen={this.state.showEditRoleModal} onClose={this.toggleEditRoleModal} title="Edit Role" icon={["fad", "edit"]}>
                    <div className="p-6">
                        <FormGroup>
                            <Label>Role Name</Label>
                            <Input type="text" value={this.state.editRoleName} onChange={(e) => this.setState({ editRoleName: e.target.value })} placeholder="Enter role name" />
                        </FormGroup>

                        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                            <Button variant="secondary" onClick={this.toggleEditRoleModal}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={this.updateRole}>
                                <FontAwesomeIcon icon={["fad", "save"]} className="mr-2" />
                                Update Role
                            </Button>
                        </div>
                    </div>
                </Modal>

                {/* Role Permissions Modal */}
                <Modal isOpen={this.state.showRolePermissionsModal} onClose={this.toggleRolePermissionsModal} title="Role Permissions" icon={["fad", "shield-check"]} size="xl">
                    <div className="p-6">
                        {this.state.selectedRole && (
                            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-semibold text-lg">
                                        {this.state.selectedRole.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900">{this.state.selectedRole.name}</div>
                                        <div className="text-sm text-gray-600">Role ID: {this.state.selectedRole.id}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <PermissionBrowser
                            key={this.state.selectedRole?.id}
                            assignedPermissions={(this.state.selectedRole?.permissions || []).map(p => p.permission)}
                            deniedPermissions={(this.state.selectedRole?.permissions || []).filter(p => p.effect === 1 || p.effect === "Deny").map(p => p.permission)}
                            canModify={name => this.canGrantPermission(name)}
                            disabledReason={this.hasPermission("manageUserPermissions") ? "You can only assign permissions you hold." : "You have view-only access."}
                            onChange={this.handlePermissionChange}
                        />

                        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                            <Button variant="secondary" onClick={this.toggleRolePermissionsModal}>
                                <FontAwesomeIcon icon={["fad", "times"]} className="mr-2" />
                                Close
                            </Button>
                        </div>
                    </div>
                </Modal>

                {/* Assign Role Modal */}
                <Modal isOpen={this.state.showAssignRoleModal} onClose={this.toggleAssignRoleModal} title="Assign Role" icon={["fad", "list-plus"]} size="lg">
                    <div className="p-6">
                        {this.state.selectedRole && (
                            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-semibold text-lg">
                                        {this.state.selectedRole.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900">{this.state.selectedRole.name}</div>
                                        <div className="text-sm text-gray-600">Add rules to determine who gets this role</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Rule Type</label>
                            <select
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                value={this.state.ruleType}
                                onChange={(e) => this.changeRuleType(e.target.value)}
                            >
                                <option value="user">Specific User</option>
                                <option value="group">Specific Group</option>
                                <option value="membership">Organization Membership</option>
                                <option value="all">All Users (*)</option>
                            </select>
                        </div>

                        {this.state.ruleType === "user" && (
                            <>
                                <div className="mb-4">
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FontAwesomeIcon icon={["fad", "search"]} className="text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                            placeholder="Search users by name or username..."
                                            value={this.state.searchQuery}
                                            onChange={this.handleQueryChange}
                                        />
                                    </div>
                                </div>

                                <div className="max-h-96 overflow-y-auto space-y-2">
                                    {this.state.users.length === 0 ? (
                                        <div className="text-center py-8">
                                            <FontAwesomeIcon icon={["fad", "users"]} className="text-4xl text-gray-300 mb-3" />
                                            <p className="text-gray-500">No users found</p>
                                            <p className="text-sm text-gray-400 mt-1">Search for users to add a rule</p>
                                        </div>
                                    ) : (
                                        this.state.users.map((user) => (
                                            <div key={user.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold">
                                                        {user.fullName ? user.fullName.charAt(0).toUpperCase() : user.userName.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="font-medium text-gray-900">{user.fullName || user.userName}</div>
                                                        <div className="text-sm text-gray-500">@{user.userName}</div>
                                                    </div>
                                                </div>
                                                <Button variant="primary" size="sm" onClick={() => this.addUserRuleToRole(user.id, user.userName)}>
                                                    <FontAwesomeIcon icon={["fad", "plus"]} className="mr-1" />
                                                    Add Rule
                                                </Button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </>
                        )}

                        {this.state.ruleType === "group" && (
                            <>
                                <input
                                    type="text"
                                    className="w-full px-4 py-3 mb-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="Search groups by name..."
                                    aria-label="Search groups"
                                    value={this.state.groupSearchQuery}
                                    onChange={(e) => this.setState({ groupSearchQuery: e.target.value })}
                                />
                                <div className="max-h-96 overflow-y-auto space-y-2">
                                    {this.state.groupsLoading ? <p className="text-sm text-gray-500">Loading groups...</p> :
                                        this.state.groupsError ? <p className="text-sm text-red-600">{this.state.groupsError}</p> :
                                            this.getAvailableGroups().length === 0 ?
                                                <p className="text-sm text-gray-500">No groups found.</p> :
                                                this.getAvailableGroups().map((group) => (
                                                        <div key={group.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
                                                            <span className="font-medium text-gray-900">{group.name}</span>
                                                            <Button variant="primary" size="sm" onClick={() => this.addGroupRuleToRole(group.id)}>Add Rule</Button>
                                                        </div>
                                                    ))}
                                </div>
                            </>
                        )}

                        {this.state.ruleType === "membership" && (
                            <div className="space-y-2">
                                {["Staff", "Student", "Parent", "Guest"].map((type) => (
                                    <div key={type} className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
                                        <span className="font-medium text-gray-900">{type}</span>
                                        <Button variant="primary" size="sm"
                                            disabled={this.state.selectedRole?.rules?.some((rule) => rule.rule === `${this.props.organization}:${type}`)}
                                            onClick={() => this.addMembershipRuleToRole(type)}>Add Rule</Button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {this.state.ruleType === "all" && (
                            <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-start gap-3">
                                    <FontAwesomeIcon icon={["fad", "info-circle"]} className="text-blue-600 text-xl mt-0.5" />
                                    <div>
                                        <h4 className="font-semibold text-blue-900 mb-1">Universal Rule (*)</h4>
                                        <p className="text-sm text-blue-800 mb-3">This rule will match all users in the organization.</p>
                                        <Button variant="primary" size="sm" disabled={this.state.selectedRole?.rules?.some((rule) => rule.rule === "*")}
                                            onClick={() => this.addUniversalRuleToRole()}>
                                            <FontAwesomeIcon icon={["fad", "plus"]} className="mr-1" />
                                            Add Universal Rule
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                            <Button variant="secondary" onClick={this.toggleAssignRoleModal}>
                                <FontAwesomeIcon icon={["fad", "times"]} className="mr-2" />
                                Close
                            </Button>
                        </div>
                    </div>
                </Modal>

                {/* Manage Rules Modal */}
                <Modal isOpen={this.state.showManageRulesModal} onClose={this.toggleManageRulesModal} title="Manage Role Rules" icon={["fad", "list-check"]} size="lg">
                    <div className="p-6">
                        {this.state.selectedRole && (
                            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-semibold text-lg">
                                        {this.state.selectedRole.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900">{this.state.selectedRole.name}</div>
                                        <div className="text-sm text-gray-600">{this.state.selectedRole.rules?.length || 0} rule(s) defined</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="max-h-96 overflow-y-auto space-y-2">
                            {(!this.state.selectedRole?.rules || this.state.selectedRole.rules.length === 0) ? (
                                <div className="text-center py-8">
                                    <FontAwesomeIcon icon={["fad", "list-check"]} className="text-4xl text-gray-300 mb-3" />
                                    <p className="text-gray-500">No rules defined</p>
                                    <p className="text-sm text-gray-400 mt-1">Add rules to determine who gets this role</p>
                                </div>
                            ) : (
                                this.state.selectedRole.rules.map((rule) => (
                                    <div key={rule.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                                        <div className="flex items-center gap-3">
                                            <FontAwesomeIcon icon={["fad", "check-circle"]} className="text-purple-600" />
                                            <div>
                                                <div className="font-medium text-gray-900 font-mono text-sm">{rule.rule}</div>
                                            </div>
                                        </div>
                                        {this.hasPermission("manageUserPermissions") && (
                                            <button
                                                type="button"
                                                className="inline-flex items-center px-2.5 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                                onClick={() => this.removeRuleFromRole(rule.id)}
                                            >
                                                <FontAwesomeIcon icon={["fad", "times"]} className="mr-1" />
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                            <Button variant="secondary" onClick={this.toggleManageRulesModal}>
                                <FontAwesomeIcon icon={["fad", "times"]} className="mr-2" />
                                Close
                            </Button>
                        </div>
                    </div>
                </Modal>

                {/* View Users Modal */}
                <Modal isOpen={this.state.showViewUsersModal} onClose={this.toggleViewUsersModal} title="Users with Role" icon={["fad", "users"]} size="lg">
                    <div className="p-6">
                        {this.state.selectedRole && (
                            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-semibold text-lg">
                                        {this.state.selectedRole.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900">{this.state.selectedRole.name}</div>
                                        <div className="text-sm text-gray-600">{this.state.roleUsers.length} user(s) match the role rules</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-start gap-2">
                                <FontAwesomeIcon icon={["fad", "info-circle"]} className="text-blue-600 text-sm mt-0.5" />
                                <p className="text-sm text-blue-800">Users are determined by the role's rules. Manage rules to control who has this role.</p>
                            </div>
                        </div>

                        <div className="max-h-96 overflow-y-auto space-y-2">
                            {this.state.roleUsers.length === 0 ? (
                                <div className="text-center py-8">
                                    <FontAwesomeIcon icon={["fad", "users"]} className="text-4xl text-gray-300 mb-3" />
                                    <p className="text-gray-500">No users match the role rules</p>
                                    <p className="text-sm text-gray-400 mt-1">Add rules to assign users to this role</p>
                                </div>
                            ) : (
                                this.state.roleUsers.map((user) => (
                                    <div key={user.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold">
                                            {user.fullName ? user.fullName.charAt(0).toUpperCase() : user.userName.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-900">{user.fullName || user.userName}</div>
                                            <div className="text-sm text-gray-500">@{user.userName}</div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                            <Button variant="secondary" onClick={this.toggleViewUsersModal}>
                                <FontAwesomeIcon icon={["fad", "times"]} className="mr-2" />
                                Close
                            </Button>
                        </div>
                    </div>
                </Modal>
            </div>
        );
    }
}
